import { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, Menu } from "lucide-react";
import { RouteUrl } from "@/configs/router/routes";
import { useAuth } from "@/auth/authProvider";
import { tournamentsQueryOptions, tournamentTeamsListQueryOptions } from "@/api/tournaments";
import { useSelectedTeam } from "@/context/teamContext";
import { useSelectedTournament } from "@/context/tournamentContext";
import { getTeamLogo } from "@/lib/utils";
import MaterialIcon from "@/components/general/common/MaterialIcon";
import { useOnboarding } from "@/components/general/Onboarding";
import { registerMobileMenuControl } from "@/components/general/Onboarding/mobileMenuControl";
import { UserFeedbackDialog } from "../common/UserFeedbackDialog";
import { NotificationBell } from "./NotificationBell";

const NAV_ITEMS = [
    { to: RouteUrl.Scouting, label: "Scouting", prefix: RouteUrl.Scouting, onboardingId: "onboarding-nav-scouting" },
    { to: RouteUrl.Kpis, label: "KPI Methodology", prefix: RouteUrl.Kpis, onboardingId: "onboarding-nav-kpi" },
];

function TeamSelector() {
    const navigate = useNavigate();
    const { teamId, teamName, setTeam } = useSelectedTeam();
    const { tournamentId: globalTournamentId } = useSelectedTournament();
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    const { data: tournaments } = useQuery(tournamentsQueryOptions(teamId));
    const mostRecentTournamentId = tournaments
        ?.filter((tournament) => tournament.matches && tournament.matches.length > 0)
        ?.reduce(
            (latest, current) => (latest == undefined || current.id > latest.id ? current : latest),
            undefined as (typeof tournaments)[0] | undefined
        )?.id;

    const activeTournamentId = globalTournamentId ?? mostRecentTournamentId;
    const { data: teams } = useQuery(tournamentTeamsListQueryOptions(activeTournamentId));

    useEffect(() => {
        if (!teamName && teams && teams.length > 0) {
            const valerenga = teams.find((team) => team.teamId === 527810);
            const defaultTeam = valerenga ?? teams[0];
            setTeam(defaultTeam.teamId, defaultTeam.teamName);
        }
    }, [teamName, teams, setTeam]);

    useEffect(() => {
        const handleClick = (event: MouseEvent) => {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, []);

    const selectedLogo = teamName ? (getTeamLogo(teamName, "light") ?? null) : null;

    return (
        <div ref={ref} className="relative">
            <button
                id="onboarding-nav-team-selector"
                onClick={() => setOpen((value) => !value)}
                className="flex items-center gap-2 h-8 px-3 rounded-md bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors cursor-pointer border border-white/20"
            >
                {selectedLogo ? (
                    <img src={selectedLogo} alt={teamName} className="h-5 w-5 object-contain rounded-sm" />
                ) : (
                    <div className="h-5 w-5 rounded-sm bg-white/20 flex items-center justify-center text-[10px] font-bold">
                        {teamName?.charAt(0) ?? "?"}
                    </div>
                )}
                <span className="max-w-[120px] truncate">{teamName ?? "Select team"}</span>
                <ChevronDown className={`h-3.5 w-3.5 opacity-70 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && teams && teams.length > 0 && (
                <div className="absolute top-full mt-2 left-0 z-50 w-52 rounded-md border border-outline-variant/20 bg-surface-container-lowest shadow-lg overflow-hidden">
                    {teams.map((team) => {
                        const logo = getTeamLogo(team.teamName) ?? null;
                        const isSelected = team.teamId === teamId;
                        return (
                            <button
                                key={team.teamId}
                                onClick={() => {
                                    setTeam(team.teamId, team.teamName);
                                    setOpen(false);
                                }}
                                className={`w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors cursor-pointer ${
                                    isSelected
                                        ? "bg-primary/10 text-primary font-semibold"
                                        : "text-on-surface hover:bg-surface-container"
                                }`}
                            >
                                {logo ? (
                                    <img
                                        src={logo}
                                        alt={team.teamName}
                                        className="h-6 w-6 object-contain flex-shrink-0"
                                    />
                                ) : (
                                    <div className="h-6 w-6 rounded-sm bg-surface-container flex items-center justify-center text-[10px] font-bold text-on-surface-variant flex-shrink-0">
                                        {team.teamName.charAt(0)}
                                    </div>
                                )}
                                <span className="truncate">{team.teamName}</span>
                            </button>
                        );
                    })}
                    <button
                        onClick={() => {
                            setOpen(false);
                            navigate({ to: RouteUrl.Teams });
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2 text-sm text-on-surface-variant hover:bg-surface-container hover:text-on-surface transition-colors cursor-pointer border-t border-outline-variant/20"
                    >
                        <span>See more...</span>
                    </button>
                </div>
            )}
        </div>
    );
}

const NavigationTop = () => {
    const { logout } = useAuth();
    const navigate = useNavigate();
    const currentPath = useRouterState({ select: (select) => select.location.pathname });
    const onboarding = useOnboarding();
    const [feedbackOpen, setFeedbackOpen] = useState(false);

    const handleGuideClick = () => {
        const tourMap: Record<string, () => void> = {
            [RouteUrl.DashboardOverview]: onboarding.startDashboardOverviewTour,
            [RouteUrl.DashboardAnalytics]: onboarding.startDashboardAnalyticsTour,
            [RouteUrl.DashboardHeatMap]: onboarding.startDashboardHeatmapTour,
            [RouteUrl.DashboardCompare]: onboarding.startDashboardCompareTour,
            [RouteUrl.DashboardTeamComparison]: onboarding.startDashboardTeamComparisonTour,
            [RouteUrl.DashboardPeriodData]: onboarding.startDashboardPeriodDataTour,
            [RouteUrl.Players]: onboarding.startPlayersTour,
            [RouteUrl.Leaderboards]: onboarding.startLeaderboardsTour,
            [RouteUrl.TournamentOverview]: onboarding.startTournamentOverviewTour,
            [RouteUrl.TournamentCompare]: onboarding.startTournamentCompareTour,
            [RouteUrl.TournamentRanking]: onboarding.startTournamentRankingTour,
            [RouteUrl.TournamentLines]: onboarding.startTournamentLinesTour,
            [RouteUrl.Teams]: onboarding.startTeamsTour,
            [RouteUrl.ScoutingPlayers]: onboarding.startScoutingPlayersTour,
            [RouteUrl.KpisCurrent]: onboarding.startKpisCurrentTour,
            [RouteUrl.KpisDefault]: onboarding.startKpisCurrentTour,
            [RouteUrl.Chat]: onboarding.startChatTour,
            [RouteUrl.SettingsMultiplier]: onboarding.startSettingsMultiplierTour,
            [RouteUrl.SettingsReport]: onboarding.startSettingsReportTour,
        };

        const teamSubPageTours: Array<[RegExp, () => void]> = [
            [/^\/teams\/[^/]+$/, onboarding.startTeamProfileTour],
            [/^\/teams\/[^/]+\/players$/, onboarding.startTeamPlayersTour],
            [/^\/teams\/[^/]+\/analysis$/, onboarding.startTeamAnalysisTour],
            [/^\/teams\/[^/]+\/compare$/, onboarding.startTeamCompareTour],
            [/^\/teams\/[^/]+\/comparison$/, onboarding.startTeamH2HTour],
        ];

        const startTour =
            tourMap[currentPath] ??
            (currentPath.startsWith(RouteUrl.PlayerProfile + "/")
                ? onboarding.startPlayerProfileTour
                : teamSubPageTours.find(([pattern]) => pattern.test(currentPath))?.[1]);
        if (startTour) {
            startTour();
        }
    };

    const isActive = (to: string, prefix: string) => currentPath === to || currentPath.startsWith(prefix + "/");

    const isSettingsActive = currentPath === RouteUrl.Settings || currentPath.startsWith(RouteUrl.Settings + "/");
    const isChatActive = currentPath === RouteUrl.Chat;

    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const mobileMenuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        return registerMobileMenuControl(
            () => setMobileMenuOpen(true),
            () => setMobileMenuOpen(false)
        );
    }, []);

    useEffect(() => {
        const handleClick = (event: MouseEvent) => {
            if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
                setMobileMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, []);

    return (
        <header className="fixed top-0 left-0 right-0 h-16 z-50 flex items-center justify-between px-6 performance-gradient shadow-[0px_4px_20px_rgba(5,0,102,0.3)]">
            <div className="flex items-center gap-6">
                <button
                    onClick={() => navigate({ to: RouteUrl.Dashboard })}
                    className="hover:opacity-90 transition-opacity cursor-pointer flex-shrink-0"
                >
                    <img src="/vif.png" alt="Vålerenga" className="h-10 w-auto" />
                </button>

                <nav className="hidden lg:flex items-center h-16 gap-1">
                    {NAV_ITEMS.map((item) => {
                        const active = isActive(item.to, item.prefix);
                        return (
                            <Link
                                key={item.to}
                                id={item.onboardingId}
                                to={item.to}
                                className={`h-full flex items-center px-3 text-sm font-medium transition-colors border-b-2 ${
                                    active
                                        ? "text-white border-[#ba0a06]"
                                        : "text-blue-200 border-transparent hover:text-white"
                                }`}
                            >
                                {item.label}
                            </Link>
                        );
                    })}
                </nav>
            </div>

            <div className="absolute left-1/2 -translate-x-1/2">
                <TeamSelector />
            </div>

            <div className="hidden lg:flex items-center gap-1">
                <NotificationBell />
                <button
                    id="onboarding-guide-icon"
                    onClick={handleGuideClick}
                    className="p-2 rounded-full text-blue-200 hover:bg-white/10 hover:text-white transition-colors cursor-pointer"
                    title="Page guide"
                >
                    <MaterialIcon name="info" className="text-xl leading-none" />
                </button>
                <button
                    id="onboarding-nav-chat"
                    onClick={() => navigate({ to: RouteUrl.Chat })}
                    className={`p-2 rounded-full transition-colors cursor-pointer ${
                        isChatActive ? "bg-white/20 text-white" : "text-blue-200 hover:bg-white/10 hover:text-white"
                    }`}
                    title="Chat"
                >
                    <MaterialIcon name="smart_toy" className="text-xl leading-none" />
                </button>
                <button
                    id="onboarding-nav-settings"
                    onClick={() => navigate({ to: RouteUrl.Settings })}
                    className={`p-2 rounded-full transition-colors cursor-pointer ${
                        isSettingsActive ? "bg-white/20 text-white" : "text-blue-200 hover:bg-white/10 hover:text-white"
                    }`}
                    title="Settings"
                >
                    <MaterialIcon name="settings" className="text-xl leading-none" />
                </button>
                <button
                    onClick={() => {
                        logout();
                        navigate({ to: RouteUrl.Login });
                    }}
                    className="p-2 rounded-full text-blue-200 hover:bg-white/10 hover:text-white transition-colors cursor-pointer"
                    title="Log out"
                >
                    <MaterialIcon name="logout" className="text-xl leading-none" />
                </button>
            </div>

            <div ref={mobileMenuRef} className="relative flex lg:hidden items-center gap-1">
                <NotificationBell />
                <button
                    onClick={() => setMobileMenuOpen((v) => !v)}
                    className="p-2 rounded-full text-blue-200 hover:bg-white/10 hover:text-white transition-colors cursor-pointer"
                    title="Menu"
                >
                    <Menu className="h-5 w-5" />
                </button>

                {mobileMenuOpen && (
                    <div className="absolute top-full mt-2 right-0 z-50 w-50 rounded-md border border-outline-variant/20 bg-surface-container-lowest shadow-lg overflow-hidden">
                        <button
                            id="onboarding-nav-scouting-mobile"
                            onClick={() => {
                                navigate({ to: RouteUrl.Scouting });
                                setMobileMenuOpen(false);
                            }}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors cursor-pointer`}
                        >
                            <MaterialIcon
                                name="group_search"
                                className="text-base leading-none text-on-surface-variant"
                            />
                            <span>Scouting</span>
                        </button>
                        <button
                            id="onboarding-nav-kpi-mobile"
                            onClick={() => {
                                navigate({ to: RouteUrl.Kpis });
                                setMobileMenuOpen(false);
                            }}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors cursor-pointer`}
                        >
                            <MaterialIcon
                                name="psychology"
                                className="text-base leading-none text-on-surface-variant"
                            />
                            <span>KPI methodology</span>
                        </button>
                        <button
                            id="onboarding-guide-icon-mobile"
                            onClick={() => {
                                handleGuideClick();
                                setMobileMenuOpen(false);
                            }}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors cursor-pointer`}
                        >
                            <MaterialIcon name="info" className="text-base leading-none text-on-surface-variant" />
                            <span>Page Guide</span>
                        </button>
                        <button
                            id="onboarding-nav-chat-mobile"
                            onClick={() => {
                                navigate({ to: RouteUrl.Chat });
                                setMobileMenuOpen(false);
                            }}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors cursor-pointer`}
                        >
                            <MaterialIcon name="smart_toy" className="text-base leading-none text-on-surface-variant" />
                            <span>Chat</span>
                        </button>
                        <button
                            id="onboarding-nav-settings-mobile"
                            onClick={() => {
                                navigate({ to: RouteUrl.Settings });
                                setMobileMenuOpen(false);
                            }}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors cursor-pointer`}
                        >
                            <MaterialIcon name="settings" className="text-base leading-none text-on-surface-variant" />
                            <span>Settings</span>
                        </button>
                        <div id="onboarding-nav-footer-mobile">
                            <button
                                onClick={() => {
                                    navigate({ to: RouteUrl.SystemStatus });
                                    setMobileMenuOpen(false);
                                }}
                                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors cursor-pointer`}
                            >
                                <MaterialIcon
                                    name="cell_wifi"
                                    className="text-base leading-none text-on-surface-variant"
                                />
                                <span>System status</span>
                            </button>
                            <button
                                onClick={() => {
                                    navigate({ to: RouteUrl.PrivacyPolicy });
                                    setMobileMenuOpen(false);
                                }}
                                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors cursor-pointer`}
                            >
                                <MaterialIcon
                                    name="privacy_tip"
                                    className="text-base leading-none text-on-surface-variant"
                                />
                                <span>Privacy policy</span>
                            </button>
                            <button
                                onClick={() => {
                                    setFeedbackOpen(true);
                                    setMobileMenuOpen(false);
                                }}
                                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors cursor-pointer`}
                            >
                                <MaterialIcon
                                    name="comment"
                                    className="text-base leading-none text-on-surface-variant"
                                />
                                <span>Feedback</span>
                            </button>
                        </div>
                        <button
                            onClick={() => {
                                logout();
                                navigate({ to: RouteUrl.Login });
                                setMobileMenuOpen(false);
                            }}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors cursor-pointer`}
                        >
                            <MaterialIcon name="logout" className="text-base leading-none text-on-surface-variant" />
                            <span>Log out</span>
                        </button>
                    </div>
                )}
            </div>
            <UserFeedbackDialog isOpen={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
        </header>
    );
};

export default NavigationTop;
